import { useState, useRef, useEffect } from 'react';

/**
 * Hook that copies text to the clipboard and exposes the last-copied value,
 * which resets to `null` two seconds after a successful copy.
 * @returns {[string|null, (text: string) => Promise<boolean>]} A tuple of the
 *   currently-copied text (or `null`) and a `copy(text)` function that copies
 *   the given text and resolves to whether the copy succeeded.
 */
export default function useCopyToClipboard() {
    const [copiedText, setCopiedText] = useState(null);
    const resetTimeoutRef = useRef(null);

    // reset the copied text state after 2 seconds
    const reset = () => {
        if (resetTimeoutRef.current) {
            clearTimeout(resetTimeoutRef.current);
        }
        resetTimeoutRef.current = setTimeout(() => {
            setCopiedText(null);
        }, 2000);
    };

    // Clear the pending reset timer on unmount so it can't fire a setState on an
    // unmounted component or leak a real timer.
    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    const copy = async (text) => {
        if (navigator?.clipboard) {
            // Try to save to clipboard then save it in the state if worked
            try {
                await navigator.clipboard.writeText(text);
                setCopiedText(text);
                reset();
                return true;
            } catch (error) {
                console.warn('Copy failed', error);
                setCopiedText(null);
                return false;
            }
        } else if (window?.clipboardData) {
            // Fallback for older browsers that support clipboardData
            try {
                window.clipboardData.setData('Text', text);
                setCopiedText(text);
                reset();
                return true;
            } catch (error) {
                console.warn('Copy failed', error);
                setCopiedText(null);
                return false;
            }
        } else {
            console.warn('Clipboard not supported');
            return false;
        }
    };

    return [copiedText, copy];
}
